import { useMemo } from 'react'
import { useEnhancedBrandStore } from '../../stores/enhancedBrandStore'
import { ParsedBrand } from '../../types/brand'
import { EyeIcon } from '@heroicons/react/24/outline'

interface SearchResultsPreviewProps {
  onRowClick?: (brand: ParsedBrand) => void
}

export function SearchResultsPreview({ onRowClick }: SearchResultsPreviewProps) {
  const { filters, filteredBrands } = useEnhancedBrandStore()

  const previewResults = useMemo(() => {
    if (!filters.searchTerm.trim()) return []
    return filteredBrands.slice(0, 3)
  }, [filteredBrands, filters.searchTerm])

  if (!filters.searchTerm.trim() || previewResults.length === 0) {
    return null
  }

  const totalResults = filteredBrands.length

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-40">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Search Results
          </h3>
          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
            {totalResults.toLocaleString()} found
          </span>
        </div>
      </div>

      {/* Results List */}
      <div className="max-h-80 overflow-y-auto">
        {previewResults.map((brand, index) => (
          <div
            key={`${brand.brand}-${index}`}
            onClick={() => onRowClick?.(brand)}
            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      {brand.brand.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {brand.brand}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    {brand.hq_location && brand.hq_location !== 'unknown' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        📍 {brand.hq_location}
                      </span>
                    )}
                    {brand.manufacturing_verification && brand.manufacturing_verification !== 'unknown' && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        brand.manufacturing_verification === 'confirmed_usa' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                        brand.manufacturing_verification === 'likely_usa' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}>
                        {brand.manufacturing_verification === 'confirmed_usa' ? 'Confirmed USA' :
                         brand.manufacturing_verification === 'likely_usa' ? 'Likely USA' :
                         brand.manufacturing_verification.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {brand.amazon_presence && brand.amazon_presence !== 'none' && brand.amazon_presence !== 'unknown' && (
                <span className="text-xs text-orange-600 dark:text-orange-400">
                  📦 Amazon
                </span>
              )}
              <button className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <EyeIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {totalResults > 3 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Showing top 3 results • Scroll down to see all {totalResults.toLocaleString()} matches
          </p>
        </div>
      )}
    </div>
  )
}